import {
  BookOpenTextIcon,
  ClipboardCheckIcon,
  HistoryIcon,
  InboxIcon,
  InfoIcon,
  KeyRoundIcon,
  LogInIcon,
  LogOutIcon,
  MapIcon,
  MenuIcon,
  MonitorIcon,
  MoonIcon,
  ShieldIcon,
  SunIcon,
  TriangleAlertIcon,
  UserRoundIcon,
} from "lucide-react";
import { useState } from "react";
import { Link, NavLink, Outlet, ScrollRestoration } from "react-router";
import { signIn, signOut, useSession } from "@/api/session";
import { Logo } from "@/components/layout/Logo";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
  TooltipProvider,
} from "@/components/ui/menus";
import { Sheet, SheetContent, SheetDescription, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { cn } from "@/lib/cn";
import { type Theme, useTheme } from "@/lib/theme";

const PUBLIC = [
  { to: "/", label: "Carte", icon: MapIcon, end: true },
  { to: "/journal", label: "Journal", icon: HistoryIcon },
  { to: "/sources", label: "Sources", icon: BookOpenTextIcon },
  { to: "/a-propos", label: "À propos", icon: InfoIcon },
];

const SPACES = [
  { to: "/valider", label: "Valider", icon: ClipboardCheckIcon, hint: "validateurs" },
  { to: "/editeur", label: "Mes lots", icon: InboxIcon, hint: "éditeurs" },
  { to: "/admin", label: "Administration", icon: ShieldIcon, hint: "administrateurs" },
];

function navClass({ isActive }: { isActive: boolean }) {
  return cn(
    "inline-flex h-8 items-center gap-1.5 rounded-md px-2.5 text-sm font-medium whitespace-nowrap transition-colors",
    isActive ? "bg-accent text-accent-foreground" : "text-muted-foreground hover:bg-muted hover:text-foreground",
  );
}

function ThemeMenu() {
  const { theme, resolved, setTheme } = useTheme();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon-sm" aria-label="Thème de l'interface">
          {resolved === "dark" ? <MoonIcon /> : <SunIcon />}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuLabel>Thème (carte de jour ou de nuit)</DropdownMenuLabel>
        <DropdownMenuRadioGroup value={theme} onValueChange={(v) => setTheme(v as Theme)}>
          <DropdownMenuRadioItem value="light">
            <SunIcon /> Clair
          </DropdownMenuRadioItem>
          <DropdownMenuRadioItem value="dark">
            <MoonIcon /> Sombre
          </DropdownMenuRadioItem>
          <DropdownMenuRadioItem value="system">
            <MonitorIcon /> Comme le système
          </DropdownMenuRadioItem>
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** Who is signed in to the back-office (decision 46): their keys and « Se déconnecter »; else « Se connecter ». */
function AccountMenu() {
  const session = useSession();
  if (session.status === "loading" || session.status === "unavailable") return null;
  if (session.status === "signed-out")
    return (
      <Button variant="ghost" size="sm" onClick={() => void signIn()}>
        <LogInIcon />
        <span className="hidden sm:inline">Se connecter</span>
      </Button>
    );
  const profile = session.user?.profile;
  const name = profile?.name ?? profile?.email ?? "Mon compte";
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" aria-label={`Compte : ${name}`}>
          <UserRoundIcon />
          <span className="hidden max-w-36 truncate 2xl:inline">{name}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuLabel>{name}</DropdownMenuLabel>
        <DropdownMenuItem asChild>
          <Link to="/compte">
            <KeyRoundIcon /> Mes clés
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => void signOut()}>
          <LogOutIcon /> Se déconnecter
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** The permanent warning of the spec (section 2): the map is not for navigation. */
export function NavigationWarning({ className }: { className?: string }) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full border border-warning/70 bg-warning/20 px-2.5 py-1 text-xs font-medium whitespace-nowrap text-warning-foreground dark:text-warning",
        className,
      )}
    >
      <TriangleAlertIcon className="size-3.5" />
      Ne pas utiliser pour la navigation
    </span>
  );
}

function MobileMenu() {
  const [open, setOpen] = useState(false);
  const item = ({ isActive }: { isActive: boolean }) =>
    cn(
      "flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium",
      isActive ? "bg-accent text-accent-foreground" : "hover:bg-muted",
    );
  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button variant="ghost" size="icon-sm" className="lg:hidden" aria-label="Menu">
          <MenuIcon />
        </Button>
      </SheetTrigger>
      <SheetContent side="left">
        <SheetTitle>
          <Logo />
        </SheetTitle>
        <SheetDescription className="sr-only">Pages de seamap.io</SheetDescription>
        <nav className="grid gap-1" onClick={() => setOpen(false)} onKeyDown={() => undefined}>
          {PUBLIC.map((p) => (
            <NavLink key={p.to} to={p.to} end={p.end} className={item}>
              <p.icon className="size-4" /> {p.label}
            </NavLink>
          ))}
          <div className="mt-4 mb-1 px-3 text-xs font-medium text-muted-foreground uppercase">Espaces</div>
          {SPACES.map((p) => (
            <NavLink key={p.to} to={p.to} className={item}>
              <p.icon className="size-4" /> {p.label}
              <span className="ml-auto text-xs text-muted-foreground">{p.hint}</span>
            </NavLink>
          ))}
        </nav>
        <NavigationWarning className="mt-auto self-start" />
      </SheetContent>
    </Sheet>
  );
}

export function AppShell() {
  return (
    <TooltipProvider delayDuration={300}>
      <div className="flex h-dvh flex-col">
        <a
          href="#contenu"
          className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-50 focus:rounded-md focus:bg-card focus:px-3 focus:py-2"
        >
          Aller au contenu
        </a>
        <header className="z-30 flex h-14 shrink-0 items-center gap-2 border-b bg-card/80 px-3 backdrop-blur supports-backdrop-filter:bg-card/70 sm:px-4">
          <MobileMenu />
          <Link
            to="/"
            className="mr-3 rounded-md outline-none focus-visible:ring-[3px] focus-visible:ring-ring/40"
            aria-label="seamap.io, accueil"
          >
            <Logo />
          </Link>
          <nav className="hidden items-center gap-0.5 lg:flex" aria-label="Pages publiques">
            {PUBLIC.map((p) => (
              <NavLink key={p.to} to={p.to} end={p.end} className={navClass}>
                <p.icon className="size-4" />
                {p.label}
              </NavLink>
            ))}
          </nav>
          <div className="flex-1" />
          <NavigationWarning className="hidden md:inline-flex" />
          <nav className="hidden items-center gap-0.5 border-l pl-2 lg:flex" aria-label="Espaces">
            {SPACES.map((p) => (
              <NavLink key={p.to} to={p.to} className={navClass}>
                <p.icon className="size-4" />
                {p.label}
              </NavLink>
            ))}
          </nav>
          <AccountMenu />
          <ThemeMenu />
        </header>
        <main id="contenu" className="flex min-h-0 flex-1 flex-col">
          <Outlet />
        </main>
      </div>
      <ScrollRestoration />
    </TooltipProvider>
  );
}
